import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../lib/axios';
import type { Module, CreateModuleDto, UpdateModuleDto } from '../../types/subscriptions';

const QUERY_KEYS = {
  modules: ['modules'] as const,
  module: (id: string) => ['modules', id] as const,
  moduleByKey: (key: string) => ['modules', 'key', key] as const,
};

export const useModules = () => {
  return useQuery({
    queryKey: QUERY_KEYS.modules,
    queryFn: async () => {
      const { data } = await axiosInstance.get<Module[]>('/subscriptions/modules');
      return data;
    },
  });
};

export const useModule = (id: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.module(id),
    queryFn: async () => {
      const { data } = await axiosInstance.get<Module>(`/subscriptions/modules/${id}`);
      return data;
    },
    enabled: !!id,
  });
};

export const useCreateModule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (module: CreateModuleDto) => {
      const { data } = await axiosInstance.post<Module>('/subscriptions/modules', module);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.modules });
    },
  });
};

export const useUpdateModule = (id: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (module: UpdateModuleDto) => {
      const { data } = await axiosInstance.patch<Module>(`/subscriptions/modules/${id}`, module);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.modules });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.module(id) });
    },
  });
};

export const useDeleteModule = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await axiosInstance.delete(`/subscriptions/modules/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.modules });
    },
  });
};
